import {
  Fragment,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import t from "../translations";
import {
  type DatasetImageFilters,
  type DatasetImageStatus,
} from "./useDatasetImageFilter";

// Each split-filter toggle option with the `train` value it applies
// (undefined clears the filter and shows both).
const SPLIT_OPTIONS = [
  { value: "all", train: undefined, label: t.SplitAll },
  { value: "train", train: true, label: t.SplitTrain },
  { value: "test", train: false, label: t.SplitTest },
] as const;

// Each detection-outcome toggle option with the `status` value it applies
// (undefined clears the filter and shows every image).
const STATUS_OPTIONS = [
  { value: "all", status: undefined, label: t.StatusAll },
  { value: "tp", status: "tp", label: t.StatusHasTp },
  { value: "fp", status: "fp", label: t.StatusOnlyFp },
] as const satisfies readonly {
  value: string;
  status: DatasetImageStatus | undefined;
  label: string;
}[];

// A labelled id offered as a filter option (an object type or a project).
export interface FilterOption {
  id: number;
  name: string;
}

// A stable empty selection so an unset filter doesn't hand a fresh array
// reference to a controlled input on every render.
const EMPTY_SELECTION: number[] = [];

interface DatasetImageFilterButtonProps {
  filters: DatasetImageFilters;
  filterActive: boolean;
  updateFilters: (changes: Partial<DatasetImageFilters>) => void;
  objectTypeOptions: FilterOption[];
  projectOptions: FilterOption[];
}

// A labelled, exclusive toggle row for one filter dimension. `value`/`onChange`
// work in the option-`value` domain; the caller maps that to the filter field.
function ToggleFilterSection({
  label,
  options,
  value,
  onChange,
  testIdPrefix,
  orientation = "horizontal",
}: {
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  value: string | undefined;
  onChange: (value: string) => void;
  testIdPrefix: string;
  orientation?: "horizontal" | "vertical";
}) {
  return (
    <Stack sx={{ gap: 1 }}>
      <Typography variant="overline" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        orientation={orientation}
        value={value}
        onChange={(_, next: string | null) => {
          if (next !== null) onChange(next);
        }}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            data-testid={`${testIdPrefix}.${option.value}`}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

// A labelled group of filter controls — the shared shape behind each section
// in the popover's second column.
function FilterFieldGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Stack sx={{ gap: 0.5 }}>
      <Typography variant="overline" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

// The dataset image list's filter control: an icon button that opens a
// popover of filter options.
export default function DatasetImageFilterButton({
  filters,
  filterActive,
  updateFilters,
  objectTypeOptions,
  projectOptions,
}: DatasetImageFilterButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const selectedTypes = filters.objectTypes ?? EMPTY_SELECTION;
  const toggleObjectType = (id: number) => {
    const next = selectedTypes.includes(id)
      ? selectedTypes.filter((typeId) => typeId !== id)
      : [...selectedTypes, id];
    updateFilters({ objectTypes: next.length ? next : undefined });
  };

  const selectedProjectIds = filters.projectIds ?? EMPTY_SELECTION;
  const selectedProjectOptions = useMemo(
    () =>
      projectOptions.filter((option) => selectedProjectIds.includes(option.id)),
    [projectOptions, selectedProjectIds],
  );

  const hasProjectOptions = projectOptions.length > 0;
  const hasObjectTypeOptions = objectTypeOptions.length > 0;

  // The popover's second column, as a list of the sections that actually
  // apply — filtering here (rather than a per-section visibility check plus
  // a separate "both present" check for the divider between them) means a
  // third id-list filter only needs an entry added to this list.
  const secondColumnSections = [
    hasProjectOptions && (
      <FilterFieldGroup key="projects" label={t.ProjectFilterLabel}>
        <Autocomplete
          multiple
          size="small"
          fullWidth
          options={projectOptions}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedProjectOptions}
          onChange={(_, value) =>
            updateFilters({
              projectIds: value.length
                ? value.map((option) => option.id)
                : undefined,
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                selectedProjectIds.length === 0
                  ? t.FilterByProject
                  : undefined
              }
            />
          )}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => {
              const { key, ...itemProps } = getItemProps({ index });
              return (
                <Chip
                  {...itemProps}
                  key={key}
                  label={option.name}
                  size="small"
                />
              );
            })
          }
          disableCloseOnSelect
          data-testid="datasetImageList.projectFilter"
        />
      </FilterFieldGroup>
    ),
    hasObjectTypeOptions && (
      <FilterFieldGroup key="objectTypes" label={t.TypeFilterLabel}>
        <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
          {objectTypeOptions.map(({ id, name }) => (
            <FormControlLabel
              key={id}
              sx={{ display: "flex", m: 0 }}
              control={
                <Checkbox
                  size="small"
                  sx={{ py: 0.25 }}
                  checked={selectedTypes.includes(id)}
                  onChange={() => toggleObjectType(id)}
                  data-testid={`datasetImageList.typeFilter.${id}`}
                />
              }
              label={<Typography variant="body2">{name}</Typography>}
            />
          ))}
        </Box>
      </FilterFieldGroup>
    ),
  ].filter((section): section is ReactElement => Boolean(section));

  return (
    <>
      <Tooltip title={t.Filter} arrow>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={t.Filter}
          data-testid="datasetImageList.filterButton"
          sx={{ color: filterActive ? "primary.main" : "text.secondary" }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        data-testid="datasetImageList.filterPopover"
      >
        <Stack sx={{ p: 2, gap: 1.5, flexDirection: "row", minWidth: 560 }}>
          <Stack sx={{ gap: 1.5, minWidth: 260, flex: 1 }}>
            <ToggleFilterSection
              label={t.SplitFilterLabel}
              options={SPLIT_OPTIONS}
              value={
                SPLIT_OPTIONS.find((o) => o.train === filters.train)?.value
              }
              onChange={(value) => {
                const option = SPLIT_OPTIONS.find((o) => o.value === value);
                if (option) updateFilters({ train: option.train });
              }}
              testIdPrefix="datasetImageList.splitFilter"
            />

            <Divider />

            <ToggleFilterSection
              label={t.StatusFilterLabel}
              options={STATUS_OPTIONS}
              value={
                STATUS_OPTIONS.find((o) => o.status === filters.status)?.value
              }
              onChange={(value) => {
                const option = STATUS_OPTIONS.find((o) => o.value === value);
                if (option) updateFilters({ status: option.status });
              }}
              testIdPrefix="datasetImageList.statusFilter"
              orientation="vertical"
            />
          </Stack>

          {secondColumnSections.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem />
              <Stack sx={{ gap: 1.5, minWidth: 300, flex: 1 }}>
                {secondColumnSections.map((section, index) => (
                  <Fragment key={section.key}>
                    {index > 0 && <Divider />}
                    {section}
                  </Fragment>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Popover>
    </>
  );
}
